import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Alert, useTheme } from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  GridCallbackDetails,
  GridColDef,
  GridFilterModel,
  GridPaginationMeta,
} from '@mui/x-data-grid-pro';
import { useChannel } from 'ably/react';
import { useDebouncedCallback } from 'use-debounce';

import { IcArrowDown, IcArrowUp } from '@/assets/images';
import {
  DataGridToolbar,
  DataGridToolbarProps,
  NoDataError,
} from '@/components';
import { useGridColumns } from '@/components/DataGrid/hooks/useGridColumns';
import { useGridPagination } from '@/components/DataGrid/hooks/useGridPagination';
import { useGridState } from '@/components/DataGrid/hooks/useGridState';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import { deepEqual } from '@/components/DataGrid/utils';
import { defaultPageLimit } from '@/config';
import { useUserStore } from '@/store';
import { colors } from '@/theme/variables';

import { dataGridStyles } from './styles';
import { DataGridProps } from './types';

export const DataGrid = ({
  columns,
  fetchData,
  columnSettings,
  setColumnSettings,
  name,
  channelName,
  cta,
  initialPageSize,
  detailPanelContent,
  hideFilter,
  hideQuickFilter,
  hiddenColumnCount = 0,
  onFilterModelChange: userFilterModelChange,
  checkboxSelection,
  rowSelectionModel,
  onRowSelectionModelChange,
  requireFilterForRecords,
  searchPlaceholder,
  refreshToken,

  // v2 additions
  noRowsMessage,
  clientMode,
  clientRows = [],
  persistSearch,
  initialData,
  ...props
}: DataGridProps) => {
  const { slots: slotsProp, slotProps: slotPropsProp, ...restProps } = props;

  const { user } = useUserStore();

  const { initialSearchValue: rawInitialSearch, updateSearchInUrl } =
    useSearchPersist();
  const initialSearchValue = persistSearch ? rawInitialSearch : '';

  const [filter, setFilter] = useState<string>(() => {
    if (initialSearchValue) {
      return JSON.stringify({
        items: [],
        quickFilterValues: [initialSearchValue],
      } as GridFilterModel);
    }
    return '';
  });
  const [sortModel, setSortModel] = useState<any[]>([]);

  const [state, dispatch] = useGridState(initialData);
  const hadInitialData = useRef(!!initialData);

  const {
    page,
    pageSize = defaultPageLimit,
    handlePageChange,
  } = useGridPagination(initialPageSize);

  const pageSizeOptions = [...new Set([pageSize, 25, 50, 100])].sort(
    (a, b) => a - b,
  );

  const {
    handleColumnChange,
    handleColumnVisibility,
    handleColumnWidthChange,
    convertToKeyVisibilityMap,
  } = useGridColumns(
    columnSettings,
    setColumnSettings,
    name,
    user?._id as string,
    hiddenColumnCount,
  );

  // Keep fetchData stable without causing refetches due to identity changes
  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  const prevRefreshTokenRef = useRef(refreshToken);

  const handleSortModelChange = (newSortModel: any) => {
    setSortModel(newSortModel);
  };

  const hasFilterValue = (value: unknown) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;

    // Covers custom filter values like { value: ... }
    if (typeof value === 'object' && value && 'value' in value) {
      const innerValue = (value as { value?: unknown }).value;
      if (typeof innerValue === 'string') return innerValue.trim() !== '';
      return innerValue !== null && innerValue !== undefined;
    }

    return true;
  };

  const onFilterChange = (
    model: GridFilterModel,
    details: GridCallbackDetails,
  ) => {
    if (userFilterModelChange) {
      userFilterModelChange(model, details);
    }

    if (!model?.items) return;

    const currentFilter = filter
      ? (JSON.parse(filter) as GridFilterModel)
      : null;
    let shouldUpdate = false;

    // Removing filters should refetch
    if (
      !model.items.length ||
      model.items.length <= (currentFilter?.items?.length ?? 0)
    ) {
      shouldUpdate = true;
    }

    const changedItems = model.items.filter(
      (item, i) => !deepEqual(item, currentFilter?.items?.[i] ?? null),
    );

    changedItems.forEach((item) => {
      if (
        hasFilterValue(item.value) ||
        item.operator === 'isEmpty' ||
        item.operator === 'isNotEmpty'
      ) {
        shouldUpdate = true;
      }
    });

    const currentQuick = currentFilter?.quickFilterValues ?? [];
    const nextQuick = model.quickFilterValues ?? [];
    if (
      nextQuick.length ||
      (currentFilter && currentQuick.length !== nextQuick.length)
    ) {
      shouldUpdate = true;
      handlePageChange({ page: 0, pageSize });
      if (persistSearch) updateSearchInUrl(nextQuick.join(' '));
    }

    if (shouldUpdate) {
      setFilter(JSON.stringify(model));
    }
  };

  const fetchGenerationRef = useRef(0);

  const fetchDataHandler = useCallback(
    async (silent = false) => {
      if (clientMode) return;
      if (!fetchDataRef.current) return;
      if (!pageSize || pageSize < 0) return;

      const thisGeneration = ++fetchGenerationRef.current;

      if (!silent) {
        dispatch({ type: 'SET_LOADING' });
      }

      const sortParam = JSON.stringify(
        Object.fromEntries(
          sortModel.map(({ field, sort }) => [field, sort === 'asc' ? 1 : -1]),
        ),
      );

      const { rows, totalCount, hasNextPage } = await fetchDataRef.current(
        page,
        pageSize,
        filter,
        sortParam,
      );

      if (thisGeneration !== fetchGenerationRef.current) return;

      dispatch({
        type: 'SET_DATA',
        payload: { rows, rowCount: totalCount, hasNextPage },
      });
    },
    [clientMode, page, pageSize, filter, sortModel, dispatch],
  );

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridProProps['getDetailPanelContent']>
  >(
    ({ row }) =>
      detailPanelContent ? createElement(detailPanelContent, { row }) : null,
    [detailPanelContent],
  );

  const getDetailPanelHeight = useCallback<
    NonNullable<DataGridProProps['getDetailPanelHeight']>
  >(() => 'auto' as const, []);

  const getTogglableColumns = useCallback(
    (c: GridColDef[]) =>
      c
        .filter(
          (column) =>
            column.field !== '__detail_panel_toggle__' &&
            column.field !== '__check__',
        )
        .map((column) => column.field),
    [],
  );

  const debouncedChannelRefetch = useDebouncedCallback(
    () => {
      if (channelName) {
        fetchDataHandler(true);
      }
    },
    500,
    { maxWait: 2000 },
  );

  try {
    useChannel(
      {
        channelName: channelName || 'noop',
        skip: !channelName,
      },
      debouncedChannelRefetch,
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('DataGrid-e:', e);
  }

  // Avoid unnecessary refetches due to function identity changes
  const isInitialRender = useRef(true);
  const prevDepsRef = useRef({ page, pageSize, filter, sortModel });

  useEffect(() => {
    if (clientMode) return;

    if (isInitialRender.current) {
      isInitialRender.current = false;
      // Always fetch on mount. If cache was provided, fetch silently so no
      // loading skeleton shows — cached rows remain visible while data refreshes.
      fetchDataHandler(hadInitialData.current);
      return;
    }

    const prevDeps = prevDepsRef.current;
    const sortModelChanged =
      JSON.stringify(prevDeps.sortModel) !== JSON.stringify(sortModel);

    if (
      prevDeps.page !== page ||
      prevDeps.pageSize !== pageSize ||
      prevDeps.filter !== filter ||
      sortModelChanged
    ) {
      fetchDataHandler();
      prevDepsRef.current = { page, pageSize, filter, sortModel };
    }
  }, [clientMode, page, pageSize, filter, sortModel, fetchDataHandler]);

  useEffect(() => {
    if (clientMode) return;

    if (prevRefreshTokenRef.current !== refreshToken) {
      prevRefreshTokenRef.current = refreshToken;
      if (!isInitialRender.current) {
        fetchDataHandler();
      }
    }
  }, [clientMode, refreshToken, fetchDataHandler]);

  const paginationMetaRef = useRef<GridPaginationMeta>();
  const paginationMeta = useMemo(() => {
    if (clientMode) return undefined;

    if (
      state.hasNextPage !== undefined &&
      paginationMetaRef.current?.hasNextPage !== state.hasNextPage
    ) {
      paginationMetaRef.current = { hasNextPage: state.hasNextPage };
    }
    return paginationMetaRef.current;
  }, [clientMode, state.hasNextPage]);

  const theme = useTheme();

  const resolvedRows = clientMode ? clientRows : state.rows;
  const resolvedRowCount = clientMode
    ? clientRows.length
    : state.rowCount ?? -1;

  const mergedSlots = useMemo<DataGridProProps['slots']>(
    () => ({
      toolbar: DataGridToolbar,
      noRowsOverlay: !requireFilterForRecords
        ? () => <NoDataError message={noRowsMessage} />
        : () => (
            <Alert severity="info" sx={{ mt: 3 }}>
              No records to display. Modify your search or filters to try again.
            </Alert>
          ),
      detailPanelExpandIcon: detailPanelContent
        ? () => <IcArrowDown color={colors.error.main} />
        : undefined,
      detailPanelCollapseIcon: detailPanelContent
        ? () => <IcArrowUp color={colors.error.main} />
        : undefined,
      ...slotsProp,
    }),
    [detailPanelContent, noRowsMessage, requireFilterForRecords, slotsProp],
  );

  const mergedSlotProps = useMemo<DataGridProProps['slotProps']>(
    () => ({
      ...slotPropsProp,
      loadingOverlay: {
        variant: 'skeleton' as const,
        noRowsVariant: 'skeleton' as const,
        ...slotPropsProp?.loadingOverlay,
      },
      columnsManagement: {
        getTogglableColumns,
        ...slotPropsProp?.columnsManagement,
      },
      toolbar: {
        cta,
        hideFilter,
        hideQuickFilter,
        filter: filter ? JSON.parse(filter) : undefined,
        searchPlaceholder,
        ...slotPropsProp?.toolbar,
      } as DataGridToolbarProps,
    }),
    [
      cta,
      filter,
      getTogglableColumns,
      hideFilter,
      hideQuickFilter,
      searchPlaceholder,
      slotPropsProp,
    ],
  );

  return (
    <DataGridPro
      rows={resolvedRows}
      rowHeight={36}
      rowCount={resolvedRowCount}
      paginationMeta={paginationMeta}
      getRowId={(row) => row._id}
      columns={columns}
      pagination
      paginationMode={clientMode ? 'client' : 'server'}
      hideFooterPagination={false}
      paginationModel={{
        pageSize,
        page: page - 1,
      }}
      pageSizeOptions={pageSizeOptions}
      filterMode={clientMode ? 'client' : 'server'}
      filterDebounceMs={800}
      disableColumnMenu
      loading={clientMode ? false : state.loading}
      slots={mergedSlots}
      slotProps={mergedSlotProps}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize,
            page: page - 1,
          },
        },
        filter: initialSearchValue
          ? {
              filterModel: {
                items: [],
                quickFilterValues: [initialSearchValue],
              },
            }
          : undefined,
      }}
      disableRowSelectionOnClick
      onFilterModelChange={onFilterChange}
      onPaginationModelChange={handlePageChange}
      onColumnOrderChange={handleColumnChange}
      onColumnVisibilityModelChange={handleColumnVisibility}
      columnVisibilityModel={convertToKeyVisibilityMap(columnSettings)}
      onColumnWidthChange={handleColumnWidthChange}
      getDetailPanelContent={
        detailPanelContent ? getDetailPanelContent : undefined
      }
      getDetailPanelHeight={
        detailPanelContent ? getDetailPanelHeight : undefined
      }
      checkboxSelection={checkboxSelection}
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={onRowSelectionModelChange}
      onSortModelChange={handleSortModelChange}
      sortingMode={clientMode ? 'client' : 'server'}
      {...restProps}
      sx={{ ...dataGridStyles(theme), ...restProps.sx }}
    />
  );
};
